import { FC } from 'react';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import useReactRouter from 'use-react-router';
import uniq from 'lodash/uniq';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'jbc-front/components/Button';

import { useEmployeeMutation as useMutation } from '@jbc-year-end-adj/2024/hooks/graphql/useMutation';
import { ServerValidationError } from '@jbc-year-end-adj/2024/hooks/graphql/error/ServerValidationError';
import { ButtonContainer } from '@jbc-year-end-adj/2024/features/questions/components/ButtonContainer';
import { FormSection } from '@jbc-year-end-adj/2024/components/form/FormSection';
import { FileField } from '@jbc-year-end-adj/2024/components/react-hook-form/FileField';

import { schema, generateDefaultValues, Schema } from './schema';
import { ANSWER_ATTACHMENTS } from './mutation';
import { Profile } from './query';
import { attachmentFiles } from './utils';
import styles from './Form.scss';

type FormProps = {
  profile: Profile;
};

export const Form: FC<FormProps> = ({ profile }) => {
  const { history } = useReactRouter();

  const methods = useForm({
    defaultValues: generateDefaultValues(profile.attachments),
    resolver: yupResolver(schema)
  });

  const {
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError
  } = methods;

  const [update, { loading }] = useMutation(ANSWER_ATTACHMENTS);

  const onSubmit: SubmitHandler<Schema> = async (data: Schema) => {
    try {
      await update({
        variables: {
          input: {
            attachments: data.attachments.map(attachment => ({
              id: attachment.id,
              file: attachment.file
            }))
          }
        }
      });
      history.push('/employee_input');
    } catch (err) {
      if (err instanceof ServerValidationError) {
        // attachmentsしかフィールドがないので、重複排除してから決め打ちでセットする
        uniq(Object.values(err.validationErrors).flat()).forEach((error, index) => {
          setError('attachments', { type: `server[${index}]`, message: `追加添付ファイル${error}` });
        });
        return;
      }
      throw err;
    }
  };

  const attachments = watch('attachments');
  const removeFile = (index: number) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setValue('attachments', newAttachments);
  };
  const handleDropFile = (files: File[]) => {
    setValue('attachments', [...attachments, ...files.map(file => ({ file }))]);
  };
  const attachedFiles = attachmentFiles(profile);

  return (
    <FormProvider {...methods}>
      <FormSection>
        <h3>添付済みファイル</h3>
        {attachedFiles.length > 0 ? (
          attachedFiles.map((attachment, index) => (
            <div className={styles.attachmentContainer} key={`attachment-${index}`}>
              <span>{attachment.name}</span>
              {attachment.files.map((file, fileIndex) => (
                <a
                  key={`attachment-${index}-file-${fileIndex}`}
                  href={file.url}
                  download
                  className={styles.downloadLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {file.filename}
                </a>
              ))}
            </div>
          ))
        ) : (
          <span>添付済みのファイルはありません。</span>
        )}

        <h3>追加添付ファイル</h3>
        <FileField name="attachments" multiple onDrop={handleDropFile} error={errors.attachments?.message}>
          {attachments && (
            <FileField.FileContainer>
              {attachments.map((attachment, index) => (
                <FileField.File key={`attachment-file-${index}`} file={attachment.file} onRemove={() => removeFile(index)} />
              ))}
            </FileField.FileContainer>
          )}
        </FileField>
      </FormSection>

      <ButtonContainer>
        <Button primary onClick={handleSubmit(onSubmit)} disabled={loading}>
          次へ
        </Button>
      </ButtonContainer>
    </FormProvider>
  );
};
